import type * as zod from 'zod';

import { DEFAULT_RECORDING_USER_ID } from '@sb/ai/constants';
import {
  createMappingFromHardwareKeyToEquipmentInfo,
  createRobotIdsMap,
} from '@sb/ai/helpers';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import {
  RecorderStateStatusEnum,
  type RecorderConfig,
  type RecorderState,
  type StepDetails,
  type StepDetailsEntry,
  type StartRecordingRequest,
  type StopRecordingRequest,
} from '@sb/sb-robot-sdk/src/codegen/typescript-axios';

import type { StepFailure } from '../../types';
import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';
import type { StepKind } from '../StepKind';

import Arguments, { type TagValue } from './Arguments';
import Variables from './Variables';

const log = makeNamespacedLog('CreateRecordingStep');

// Type guard to check if a tag is a TagExpression
const isTagExpression = (
  tag: TagValue,
): tag is Extract<TagValue, { kind: 'TagExpression' }> => {
  return typeof tag !== 'string' && tag.kind === 'TagExpression';
};

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class CreateRecordingStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  /**
   * Track whether recording was successfully started
   * @internal
   */
  private recordingInProgress: boolean = false;

  /**
   * Timeout handle for automatic recording termination
   * @internal
   */
  private timeoutHandle: NodeJS.Timeout | null = null;

  /**
   * Recording start timestamp for calculating step offsets
   * @internal
   */
  private recordingStartTime: number = 0;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public hasSelfTimeout(): boolean {
    return false;
  }

  /**
   * Collect static string tags from the arguments
   * @internal
   */
  private collectStaticTags(): string[] {
    const staticTags: string[] = [];

    for (const tag of this.args.tags) {
      if (typeof tag === 'string') {
        staticTags.push(tag);
      }
    }

    return staticTags;
  }

  /**
   * Evaluate expression tags and return the resulting string tags
   * @internal
   */
  private async evaluateExpressionTags(): Promise<string[]> {
    const evaluatedExpressionTags: string[] = [];

    for (const tag of this.args.tags) {
      if (isTagExpression(tag)) {
        try {
          const value = await this.routineContext.evaluateExpression(
            tag.expression,
            undefined,
            true, // noFinalFailAndThrow - don't fail routine
          );

          // Convert value to string if it's not already
          let stringValue: string;

          if (typeof value === 'string') {
            stringValue = value;
          } else if (value != null) {
            stringValue = String(value);
          } else {
            log.warn(
              'recording.tags',
              'Expression tag evaluated to null/undefined',
              {
                tag,
              },
            );

            continue;
          }

          if (stringValue.trim().length > 0) {
            evaluatedExpressionTags.push(stringValue.slice(0, 30)); // Enforce max length
          }
        } catch (error) {
          log.warn('recording.tags', 'Failed to evaluate expression tag', {
            error,
            tag,
          });
        }
      }
    }

    log.info('recording.tags', 'Evaluated expression tags', {
      evaluatedExpressionTags,
    });

    return evaluatedExpressionTags;
  }

  public async _stop({
    fail,
  }: Partial<StepPlayArguments> & Pick<StepAction, 'fail'>): Promise<void> {
    // Clear timeout if step is stopped early
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    if (this.recordingInProgress) {
      const routineStatus = this.routineContext.getRoutineRunnerState().kind;
      const tags: string[] = [];

      // Add status tags
      if (routineStatus === 'Failure') {
        tags.push('error');
      } else if (routineStatus !== 'RoutineRunning') {
        tags.push('stopped');
      }

      // Evaluate expression tags if possible (substeps may not have completed)
      const evaluatedExpressionTags = await this.evaluateExpressionTags();

      // Combine status tags and evaluated expression tags
      const allTags = [...evaluatedExpressionTags, ...tags];

      // Try to collect step details if substeps have started
      const stepDetails = this.collectStepTimings();

      await this.stopRecording({ tags: allTags, stepDetails, fail });
    } else {
      log.warn(
        '_stop',
        'Skipping stop record because recording is not in progress.',
        { recordingInProgress: this.recordingInProgress },
      );
    }
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CreateRecording',
        },
        failureReason: 'CreateRecording step not supported in visualizer',
      });
    }

    try {
      const recorderApi = this.getRecorderApi();

      // Start recording before playing substeps
      await this.startRecording();

      // Store recording start time for calculating step offsets
      this.recordingStartTime = Date.now();

      // Set up timeout if specified
      if (this.args.timeout && this.args.timeout > 0) {
        this.timeoutHandle = setTimeout(async () => {
          log.warn(
            'recording.timeout',
            `Recording timeout reached (${this.args.timeout}s), stopping recording and failing routine`,
            {
              timeout: this.args.timeout,
              taskId: this.args.taskId,
            },
          );

          // Evaluate expression tags before stopping due to timeout
          const evaluatedExpressionTags = await this.evaluateExpressionTags();

          // Combine evaluated expression tags with timeout tag
          const allTags = [...evaluatedExpressionTags, 'timeout'];

          // Try to collect step details if substeps have started
          const stepDetails = this.collectStepTimings();

          await this.stopRecording({ tags: allTags, stepDetails, fail });

          // Fail the routine
          await fail({
            failure: {
              kind: FailureKind.StepPlayFailure,
              stepKind: 'CreateRecording' as const,
            },
            failureReason: `Recording timeout exceeded (${this.args.timeout}s)`,
          });
        }, this.args.timeout * 1000); // Convert seconds to milliseconds
      }

      // Status monitoring - in case recording fails
      this.startMonitoring(
        async () => {
          try {
            const res = await recorderApi.getRecorderState();

            const state = res.data;

            log.info('recording.status', 'Recording state', {
              state,
            });

            return {
              triggered:
                state?.status !== RecorderStateStatusEnum.Recording &&
                state?.status !== RecorderStateStatusEnum.Initializing,
              context: state,
            };
          } catch (err) {
            // Consider transport/other errors as non-recording
            log.error('recording.status', 'Error fetching recorder state', err);

            return {
              triggered: true,
            };
          }
        },
        {
          onTriggered: async ({ stopMonitoring }, context) => {
            stopMonitoring();

            try {
              const state = context as RecorderState;

              let errorsMsg = '';

              if (state?.errors?.length) {
                errorsMsg = `errors: ${state?.errors?.join(', ')}`;
              }

              const failureReason = `Recording failed or stopped (status=${state?.status ?? 'unknown'}). ${errorsMsg}`;

              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: 'CreateRecording',
                },
                failureReason,
              });
            } catch (err) {
              await fail({
                failure: {
                  kind: FailureKind.StepPlayFailure,
                  stepKind: 'CreateRecording',
                },
                failureReason: `Recording status check failed: ${String((err as any)?.message ?? err)}`,
                error: err as any,
              });
            }
          },
        },
        { pollIntervalMs: 1000 },
      );
    } catch (error) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'CreateRecording',
        },
        failureReason: `${error?.message}`,
        error,
      });
    }

    await playSubSteps();

    // Clear timeout since substeps completed normally
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }

    // Recording stop, monitoring stop, etc. all happen gracefully when the step is stopped by routine runner.
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    const routineState = this.routineContext.getRoutineRunnerState();

    // Check if this is an "expected" pause that should continue recording
    if (routineState.kind === 'RoutineRunning') {
      const safeStepKinds: StepKind[] = ['Antigravity', 'WaitForConfirmation'];
      const { currentStepKind } = routineState;

      if (currentStepKind && safeStepKinds.includes(currentStepKind)) {
        log.info(
          '_pause',
          'Pausing but continuing recording (safe substep triggered pause)',
          { currentStepKind },
        );

        return; // Don't stop the recording
      }
    }

    log.warn(
      '_pause',
      'Pause on CreateRecording step is not supported, stopping step.',
    );

    await this._stop({ fail });
  }

  /**
   * Recursively collect all steps (including nested substeps) and their details
   * @param steps - Steps to collect from
   * @param parentStepIds - IDs of parent steps in the hierarchy
   * @returns StepDetails object containing the entries
   */
  private collectAllStepsRecursive(
    steps: Array<Step<object, object>>,
    parentStepIds: string[] = [],
  ): StepDetails {
    const now = Date.now();
    const allEntries: Array<StepDetailsEntry> = [];

    for (const step of steps) {
      // Only include steps that were played during this recording
      if (step.playStartTime < this.recordingStartTime) {
        continue;
      }

      const startOffsetSec =
        (step.playStartTime - this.recordingStartTime) / 1000;

      // Calculate elapsed time: use timer value if available, otherwise calculate from play start
      const fullTimerValue = step.timers.full.getTime();
      const selfTimerValue = step.timers.self.getTime();

      const elapsedMs =
        fullTimerValue > 0 ? fullTimerValue : now - step.playStartTime;

      const endOffsetSec =
        (step.playStartTime - this.recordingStartTime + elapsedMs) / 1000;

      const selfTimeSec =
        selfTimerValue > 0 ? selfTimerValue / 1000 : undefined;

      // Get parent step IDs (excluding CreateRecording itself)
      const parentIds = parentStepIds.filter((id) => id !== this.id);

      // Serialize step arguments (limit size to avoid huge payloads)
      let stepArgs: string | undefined;

      try {
        const argsJson = JSON.stringify(step.args);

        // Limit to 10KB to avoid huge payloads
        if (argsJson.length <= 10000) {
          stepArgs = argsJson;
        }
      } catch (error) {
        log.warn('recording.stepTimings', 'Failed to serialize step args', {
          stepId: step.id,
          error,
        });
      }

      // Serialize step variables (limit size) - use toJSON() to get clean variables without stepKind tag
      let stepVariables: string | undefined;

      try {
        const { variables } = step.toJSON();

        if (variables && Object.keys(variables).length > 0) {
          const varsJson = JSON.stringify(variables);

          // Limit to 10KB
          if (varsJson.length <= 10000) {
            stepVariables = varsJson;
          }
        }
      } catch (error) {
        log.warn(
          'recording.stepTimings',
          'Failed to serialize step variables',
          {
            stepId: step.id,
            error,
          },
        );
      }

      allEntries.push({
        stepId: step.id,
        stepKind: step.getStepKind(),
        startOffsetSec,
        endOffsetSec,
        selfTimeSec,
        parentStepIds: parentIds,
        stepArgs,
        stepVariables,
      });

      // Recursively collect nested substeps
      if (step.substeps && step.substeps.length > 0) {
        const nestedDetails = this.collectAllStepsRecursive(step.substeps, [
          ...parentStepIds,
          step.id,
        ]);

        allEntries.push(...nestedDetails.entries);
      }
    }

    return { entries: allEntries };
  }

  /**
   * Collect timing data from substeps (recursively including all nested substeps)
   * @returns StepDetails object containing the entries, or undefined if no timings
   */
  private collectStepTimings(): StepDetails | undefined {
    if (!this.substeps || this.substeps.length === 0) {
      log.info('recording.stepTimings', 'No substeps to collect timing from');

      return undefined;
    }

    const stepDetails = this.collectAllStepsRecursive(this.substeps, [this.id]);

    if (stepDetails.entries.length === 0) {
      log.info('recording.stepTimings', 'No step timings collected');

      return undefined;
    }

    log.info('recording.stepTimings', 'Collected step timings', {
      count: stepDetails.entries.length,
      timings: stepDetails.entries.map((t: StepDetailsEntry) => ({
        stepId: t.stepId,
        stepKind: t.stepKind,
        startOffsetSec: t.startOffsetSec,
        endOffsetSec: t.endOffsetSec,
        selfTimeSec: t.selfTimeSec,
        parentStepIds: t.parentStepIds,
        hasArgs: !!t.stepArgs,
        hasVariables: !!t.stepVariables,
      })),
    });

    return stepDetails;
  }

  private async startRecording() {
    log.info('recording.start', 'Starting recording for CreateRecording step', {
      taskId: this.args.taskId,
    });

    const allEquipmentState = this.routineContext.equipment.getEquipmentState();

    const robotIdsMap = createRobotIdsMap(
      this.args.hardwareConfigEquipmentMapping,
      this.routineContext.robotId || 'local',
      allEquipmentState,
    );

    const equipmentKeyToEquipmentInfo =
      createMappingFromHardwareKeyToEquipmentInfo(
        this.args.hardwareConfigEquipmentMapping,
      );

    const equipmentKeyToEquipmentId = Object.entries(
      equipmentKeyToEquipmentInfo,
    ).reduce(
      (acc, [key, equipmentInfo]) => {
        acc[key] = equipmentInfo.equipmentId;

        return acc;
      },
      {} as Record<string, string>,
    );

    const botIds = Object.values(robotIdsMap);

    const secondaryBotIds = botIds.filter(
      (id) => id !== this.routineContext.robotId,
    );

    try {
      const recorderApi = this.getRecorderApi();

      // Only add static string tags at the start - expression tags will be evaluated at the end
      const staticTags = this.collectStaticTags();

      // Enable secondary bots if any are specified in the hardware config mapping
      await this.ensureSecondaryBotsEnabled(recorderApi, secondaryBotIds);

      // First, configure the recording with task details
      const configureRequest: RecorderConfig = {
        taskId: this.args.taskId || '',
        userId: this.args.userId || DEFAULT_RECORDING_USER_ID,
        isSecondary: false,
        bots: secondaryBotIds,
        sensors: [],
        equipmentKeyAssignments: equipmentKeyToEquipmentId,
        tags: ['routine', ...staticTags], // Tag this as a routine recording with static tags
      };

      log.info('recording.configure', 'Configuring recording', {
        taskId: this.args.taskId,
        equipmentKeyAssignments: equipmentKeyToEquipmentId,
        tags: ['routine', ...staticTags],
      });

      const configureResponse =
        await recorderApi.updateRecording(configureRequest);

      if (!configureResponse.data.success) {
        const errors =
          configureResponse.data.errors ||
          configureResponse.data.state?.errors ||
          [];

        log.error('recording.configure', 'Failed to configure recording', {
          errors,
        });

        throw new Error(
          `Failed to configure recording: ${Array.isArray(errors) ? errors.join(', ') : String(errors)}`,
        );
      }

      log.info('recording.configure', 'Successfully configured recording');

      // Then start the actual recording
      const recordingStartTime = new Date();

      const startRecordingRequest: StartRecordingRequest = {
        startTime: recordingStartTime.toISOString(),
        equipmentKeyAssignments: equipmentKeyToEquipmentId,
      };

      log.info('recording.start', 'Starting recording via recorder API');

      this.recordingInProgress = true;

      const startRecordingResponse = await recorderApi.startRecording(
        startRecordingRequest,
      );

      const startState = startRecordingResponse.data.state;

      if (!startRecordingResponse.data.success) {
        throw new Error('Failed to start recording via recorder API');
      }

      if (
        !startState ||
        (startState.status !== RecorderStateStatusEnum.Recording &&
          startState.status !== RecorderStateStatusEnum.Initializing)
      ) {
        log.error('recording.start', 'Failed to start recording', {
          status: startState?.status,
          errors: startState?.errors,
        });

        throw new Error(
          `Failed to start recording: ${startState?.status ?? 'unknown'}. errors: ${startState?.errors?.join(', ')}`,
        );
      }

      log.info(
        'recording.start',
        'Successfully started recording via echo-bot',
      );
    } catch (error) {
      log.error(
        'recording.start',
        'Failed to start recording via recorder API',
        error,
      );

      this.recordingInProgress = false;

      throw error;
    }
  }

  /**
   * Ensures that secondary bots specified in the hardware config mapping are enabled
   * for recording, and disables any bots that are enabled but not specified.
   *
   * @param recorderApi - The recorder API instance
   * @param secondaryBotIds - Array of secondary bot IDs to enable
   * @throws Error if any bot enablement/disablement fails or if recorder state cannot be retrieved
   */
  private async ensureSecondaryBotsEnabled(
    recorderApi: ReturnType<typeof this.getRecorderApi>,
    secondaryBotIds: string[],
  ): Promise<void> {
    const currentStateResponse = await recorderApi.getRecorderState();
    const currentState = currentStateResponse.data;
    const enabledBots = currentState?.config?.bots || [];

    // Handle both string[] and RecorderBotDetails[] formats
    const enabledBotIdStrings = enabledBots
      .map((bot: string | { botId?: string; isEnabled?: boolean }) => {
        if (typeof bot === 'string') {
          return bot;
        }

        if (bot.isEnabled) {
          return bot.botId;
        }

        return undefined;
      })
      .filter((id: string | undefined): id is string => Boolean(id));

    log.info('recording.enableBot', 'Checking secondary bots', {
      secondaryBotIds,
      enabledBotIdStrings,
    });

    // Collect bots that need to be enabled
    const botsToEnable = secondaryBotIds.filter(
      (botId) => !enabledBotIdStrings.includes(botId),
    );

    // Log bots that are already enabled
    const alreadyEnabledBots = secondaryBotIds.filter((botId) =>
      enabledBotIdStrings.includes(botId),
    );

    for (const botId of alreadyEnabledBots) {
      log.info(
        'recording.enableBot',
        `Secondary bot ${botId} is already enabled`,
      );
    }

    // Collect bots that need to be disabled
    const botsToDisable = enabledBotIdStrings.filter(
      (botId: string) => !secondaryBotIds.includes(botId),
    );

    // Enable all bots in parallel
    const enablePromises = botsToEnable.map(async (botId) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: true });
        log.info('recording.enableBot', `Enabled secondary bot ${botId}`);
      } catch (enableError) {
        log.error(
          'recording.enableBot',
          `Failed to enable bot ${botId}`,
          enableError,
        );

        throw new Error(
          `Failed to enable secondary bot ${botId} for recording: ${(enableError as Error)?.message ?? String(enableError)}`,
        );
      }
    });

    // Disable all bots in parallel
    const disablePromises = botsToDisable.map(async (botId: string) => {
      try {
        await recorderApi.enableRecorderBot({ botId, enabled: false });
        log.info('recording.enableBot', `Disabled secondary bot ${botId}`);
      } catch (disableError) {
        log.error(
          'recording.enableBot',
          `Failed to disable bot ${botId}`,
          disableError,
        );

        throw new Error(
          `Failed to disable secondary bot ${botId}: ${(disableError as Error)?.message ?? String(disableError)}`,
        );
      }
    });

    // Execute all enable and disable operations in parallel
    try {
      await Promise.all([...enablePromises, ...disablePromises]);
    } catch (error) {
      const errorMessage =
        (error as Error)?.message ?? String(error) ?? 'Unknown error';

      log.error(
        'recording.enableBot',
        'Failed to enable/disable secondary bots',
        {
          error,
          errorMessage,
          botsToEnable,
          botsToDisable,
        },
      );

      throw new Error(
        `Failed to configure secondary bots for bimanual recording: ${errorMessage}`,
      );
    }
  }

  private async stopRecording({
    tags = [],
    stepDetails,
    fail,
  }: {
    tags?: string[];
    stepDetails?: StepDetails;
    fail: (failure: StepFailure) => Promise<void>;
  }) {
    if (this.recordingInProgress) {
      try {
        const recorderApi = this.getRecorderApi();

        this.recordingInProgress = false;

        const stopRequest: StopRecordingRequest = {
          delete: !this.args.uploadRecording,
          tags,
          stepDetails,
          waitForSave: this.args.waitForUpload,
        };

        const stopResponse = await recorderApi.stopRecording(stopRequest);

        log.info('recording.stop', 'Stopped recording', {
          data: stopResponse.data,
        });
      } catch (recordingError) {
        await fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'CreateRecording',
          },
          failureReason: 'Failed to stop recording',
          error: recordingError as Error,
        });
      }
    } else {
      log.info('recording.stop', 'Recording not started, skipping stop');
    }
  }

  private getRecorderApi() {
    const { sdk } = this.routineContext;

    if (!sdk) {
      log.error('recording.stop', 'Public API SDK not available');

      throw new Error('Public API SDK not available');
    }

    const recorderApi = sdk.recorder;

    if (!recorderApi) {
      log.error('recording.api', 'Recorder API not available on SDK');

      throw new Error('Recorder API not available on SDK');
    }

    return recorderApi;
  }
}
